import React, { useContext, useEffect, useState } from 'react';
import { useProductDetailResources } from '@/hooks/useProductDetailResources';
import {
  transformProduct,
  getProductAlternativeImages,
  addPrimaryImage,
  initMyoProducts,
  createStringArray,
  addProduct,
  removeProduct,
  openListSideSheet,
} from '@/utils/products';
import {
  ProductDetailsImageSection,
  ProductInfo,
  ProductVideo,
  NutritionFacts,
  ProductContentGroup,
  ProductCustomization,
  ProductDetailsAsideSkeleton,
  SnackBar,
  Heading,
  HeadingEnum,
  TextColorEnum,
  IconSizeEnum,
  Icon,
  ArrowBack,
} from '@desquared/wave-components-library';
import { BundleOfferSideSheetStore } from '@/store/sideSheet';
import { SelectedBundleOfferStore } from '@/store/selectedBundleOfferId';
import useBundleOffersSummary from '@/hooks/useBundleOffersSummary';
import useProductTags from '@/hooks/useProductTags';
import { useTranslations } from 'next-intl';
import productStyles from '@/styles/Products.module.css';
import useWindowUnderThreshold from '@/hooks/useWindowUnderThreshold';
import cn from 'classnames';
import styles from './ProductDetailsAside.module.css';
import useDisableBodyScroll from '@/hooks/useDisableBodyScroll';
import { productsForOrder } from '@/store/cart';
import UserListsStore from '@/store/userLists';
import { getOrFetchConfiguration } from '@/utils/getConfiguration';
import { CurrencyContext } from '@/contexts/CurrencyProvider';
import { useRouter } from 'next/router';
import { useConfiguration } from '@/contexts/ConfigContext';
import { useQuery } from '@tanstack/react-query';
import { getProductInfoV3, getProductPriceHistory } from '@/api/Products';
import { checkIfInCart } from '@/utils/products';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import { usePersonalizedProductsIds } from '@/contexts/PersonalizedProductsIdsContext.jsx';
import { createAvailabilityTextDetails, describeDeliveryRule } from '@/utils/products/products';

import Link from 'next/link.js';
// import { getFrequentlyBoughtTogether, getSimilarProducts } from '@/api/lists';
// import useSimilarAndFrequentProducts from '@/hooks/useSimilarAndFrequentProducts';

export function ProductDetailsAside({
  id,
  finalPrice,
  beginPrice,
  onBack,
  defaultIngredientsArray,
  handleProductSelect,
}) {
  const { formatter } = useContext(CurrencyContext);
  const { config: configuration } = useConfiguration();
  const router = useRouter();
  const { productInfo, isLoadingProductInfo, productDetails, isLoadingProductDetails } = useProductDetailResources(
    id,
    router.locale
  );
  const [textAreaValue, setTextAreaValue] = useState('');
  const [comments, setComments] = useState([]);
  const [createdProducts, setCreatedProducts] = useState([]);
  const [optionsGroups, setOptionsGroups] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [toastMessage, setToastMessage] = useState('');
  const { productTags } = useProductTags();
  const { bundleOffersSummary } = useBundleOffersSummary();
  const [productsOrder, setProductsOrder] = useState();
  const [lists, setLists] = useState([]);
  const { personalizedProductsIds } = usePersonalizedProductsIds();

  const { data: packagedProduct } = useQuery({
    queryKey: ['packagedProduct', productInfo?.packagingProductId, router.locale],
    queryFn: ({ queryKey }) =>
      getProductInfoV3(productInfo?.packagingProductId, router.locale, process.env.PRIVATE_API_KEY),
    staleTime: 1000 * 60 * 30,
    enabled: !!productInfo?.packagingProductId && !!configuration?.catalog?.product?.packagingFee?.enabled,
  });

  const a = useTranslations('common.asides');
  const t = useTranslations();

  const lightBoxAriaLabels = {
    closeAriaLabel: t('common.asides.side_cart.close'),
    previousSlideAriaLabel: t('common.previous_slide_aria_label'),
    nextSlideAriaLabel: t('common.next_slide_aria_label'),
    carouselAriaLabel: t('common.carousel_aria_label'),
  };

  const [productFormatted, setProductFormatted] = useState({
    ...productInfo,
    ...transformProduct(
      productInfo,
      null,
      undefined,
      productTags,
      bundleOffersSummary,
      undefined,
      undefined,
      defaultIngredientsArray,
      personalizedProductsIds,
      a
    ),
  });
  const [selectedOptions, setSelectedOptions] = useState({});
  const [hasBundleOffer, setHasBundleOffer] = useState(false);
  const [bundleDescription, setBundleDescription] = useState('');
  const [showSnackBar, setShowSnackBar] = useState(false);
  const isMobile = useWindowUnderThreshold(850);
  const images = getProductAlternativeImages({
    ...productInfo,
    ...transformProduct(
      productInfo,
      null,
      undefined,
      productTags,
      bundleOffersSummary,
      undefined,
      undefined,
      defaultIngredientsArray,
      personalizedProductsIds,
      a
    ),
  });
  const imagesWithPrimary = addPrimaryImage(productFormatted, images);
  const shouldDisplayNutritionFacts = productDetails?.nutritionItems?.columns?.titles?.length > 0;
  const shouldDisplayProductContentGroups = productDetails?.contentGroups && productDetails?.contentGroups.length;
  const [config, setConfig] = useState();
  const [hasCartAdditionText, setHasCartAdditionText] = useState(false);
  const [shouldDisplayPriceHistory, setShouldDisplayPriceHistory] = useState(false);
  const [oldPrice, setOldPrice] = useState();
  const [ingredientDefaultValues, setIngredientDefaultValues] = useState(undefined);

  const [showBarcode, setShowBarcode] = useState(false);
  const [product, setProduct] = useState({
    id: productFormatted._id,
    comments: comments[0],
    ...productFormatted,
    oldPrice:
      productFormatted.optionsGroups?.length > 0
        ? productFormatted.optionsGroups[0].options[selectedIndex].association?.beginPricePerStepToUom ===
          productFormatted.optionsGroups[0].options[selectedIndex].association?.finalPricePerStepToUom
          ? productFormatted.optionsGroups[0].options[selectedIndex].association?.beginPricePerStepToUom
          : undefined
        : productFormatted.finalPrice !== productFormatted.beginPrice
        ? (productFormatted.beginPrice / 100).toFixed(2)
        : undefined,
  });

  const unwrapIngredientsArray = ingredientsArray => {
    if (!product.ingredientsGroups) return {};

    let initialValues = {};
    for (const group of product.ingredientsGroups) {
      const isExclusive = group.maxOptionsAllowed === 1 && group.minOptionsAllowed === 1;

      for (const option of group.options) {
        if (ingredientsArray.includes(option.id) && option.available) {
          if (Object.hasOwn(initialValues, group.id)) initialValues[group.id].push(option.id);
          else {
            initialValues[group.id] = isExclusive ? option.id : [option.id];
          }
        }
      }

      if (!(group.id in initialValues)) {
        initialValues[group.id] = isExclusive ? '' : [];
      }
    }

    return initialValues;
  };

  const flattenIngredientsArray = ingredientsValues => {
    const ingredients = [];
    for (const key in ingredientsValues) {
      for (const ingredient of ingredientsValues[key]) if (ingredient) ingredients.push(ingredient);
    }

    return ingredients;
  };

  useEffect(() => {
    if (!product) return;
    setIngredientDefaultValues(unwrapIngredientsArray(defaultIngredientsArray));
  }, [product]);

  const { data: productPriceHistory } = useQuery({
    queryKey: ['product-price-history', id],
    queryFn: async () => {
      const today = dayjs();
      const oneMonthBefore = today.subtract(1, 'month');

      return await getProductPriceHistory(id, oneMonthBefore.format('YYYY-MM-DD'), today.format('YYYY-MM-DD'));
    },
    select: data => {
      if (!data.data?.length) return null;

      return Math.min(...data.data.map(p => p.price)) / 100;
    },
    enabled: !!configuration?.catalog?.product?.details?.showPriceHistory,
  });

  useEffect(() => {
    if (configuration?.catalog?.product?.details?.showPriceHistory) setShouldDisplayPriceHistory(true);
  }, [configuration, setShouldDisplayPriceHistory]);

  const handleProductChange = (product, action, ingredients = null) => {
    let productWithIngredients = { ...productFormatted };
    if (ingredients) {
      productWithIngredients = { ...productWithIngredients, ingredients: flattenIngredientsArray(ingredients) };
    }

    if (action === 'add') {
      if (productFormatted.optionsGroups.length > 0) {
        addProduct(createdProducts[selectedIndex]);
        setCreatedProducts(initMyoProducts(productFormatted, comments));
        getOptions(productFormatted?.optionsGroups);
        setToastMessage(createStringArray(optionsGroups[0].options, createdProducts, t));
      } else {
        addProduct(
          {
            id: productWithIngredients._id,
            ...productWithIngredients,
            comments: comments[0],
          },
          undefined,
          undefined,
          undefined,
          undefined,
          true
        );
        setProductFormatted({
          ...productInfo,
          ...transformProduct(
            productInfo,
            null,
            undefined,
            productTags,
            bundleOffersSummary,
            finalPrice,
            beginPrice,
            ingredients ? flattenIngredientsArray(ingredients) : undefined,
            personalizedProductsIds,
            a
          ),
        });
      }
      if (process.env.hasItemAdditionMessage) {
        setHasCartAdditionText(true);
      }
    } else if (action === 'remove') {
      if (productFormatted.optionsGroups.length > 0) {
        removeProduct(createdProducts[selectedIndex]);
        setCreatedProducts(initMyoProducts(productFormatted));
        getOptions(productFormatted?.optionsGroups);
        if (process.env.hasItemAdditionMessage) {
          setHasCartAdditionText(removeAddText(productFormatted));
        }
      } else {
        removeProduct(productWithIngredients, undefined, undefined, true);
        setProductFormatted({
          ...productInfo,
          ...transformProduct(
            productInfo,
            null,
            undefined,
            productTags,
            bundleOffersSummary,
            finalPrice,
            beginPrice,
            ingredients ? flattenIngredientsArray(ingredients) : undefined,
            personalizedProductsIds,
            a
          ),
        });
        if (process.env.hasItemAdditionMessage) {
          setHasCartAdditionText(removeAddText(product));
        }
      }
    }

    setProduct({
      id: productInfo._id,
      ...productInfo,
      ...transformProduct(
        productInfo,
        null,
        undefined,
        productTags,
        bundleOffersSummary,
        finalPrice,
        beginPrice,
        undefined,
        personalizedProductsIds,
        a
      ),
      oldPrice:
        productFormatted.optionsGroups.length > 0
          ? productFormatted.optionsGroups[0].options[selectedIndex].association?.beginPricePerStepToUom ===
            productFormatted.optionsGroups[0].options[selectedIndex].association?.finalPricePerStepToUom
            ? productFormatted.optionsGroups[0].options[selectedIndex].association?.beginPricePerStepToUom
            : undefined
          : productFormatted.finalPrice !== productFormatted.beginPrice
          ? (productFormatted.beginPrice / 100).toFixed(2)
          : undefined,
    });
  };

  const ingredientsAddAsNew = ingredients => {
    handleProductChange(product, 'add', ingredients);

    handleProductSelect(id, finalPrice, beginPrice, flattenIngredientsArray(ingredients));
  };

  const ingredientsUpdateCurrent = ingredients => {
    let count = 0;
    const defaultIngredientArray = [];

    for (const key in ingredientDefaultValues) {
      for (const ingredient of ingredientDefaultValues[key]) {
        defaultIngredientArray.push(ingredient);
      }
    }

    while (checkIfInCart({ ...product, ingredients: defaultIngredientArray })) {
      handleProductChange(product, 'remove', ingredientDefaultValues);
      count++;
    }

    for (let i = 0; i < count; i++) {
      handleProductChange(product, 'add', ingredients);
    }

    handleProductSelect(id, finalPrice, beginPrice, flattenIngredientsArray(ingredients));
  };

  const removeAddText = product => {
    const items = productsForOrder.getValue();
    const found = items.find(i => i._id === product._id);
    return !!found;
  };

  const getOptions = groups => {
    if (groups.length > 0) {
      const firstGroup = groups[0];

      const groupOptions = firstGroup.options.map((option, index) => {
        return {
          text: option.label,
          selected: selectedOptions[firstGroup.id] === option.id || index === selectedIndex,
          isOpted: createdProducts[index]?.modifiedUom > 0,
        };
      });

      const optionsData = [
        {
          title: firstGroup.label,
          options: groupOptions,
        },
      ];

      setOptionsGroups(optionsData);

      const defaultSelectedOption = groupOptions[0]?.text || '';
      setSelectedOptions({ [firstGroup.id]: defaultSelectedOption });
      if (optionsData[0].options.length > 0) {
        setComments(optionsData[0]?.options?.map(el => ''));
        setToastMessage(createStringArray(optionsData[0]?.options, createdProducts, t));
      }
    }
  };

  const handleCommentChange = comment2 => {
    setComments(prevComments => {
      const updatedComments = [...prevComments];
      updatedComments[selectedIndex] = comment2;
      return updatedComments;
    });

    // TODO: update entries for submission
  };

  const selectOption = (group, index) => {
    const newOptions = optionsGroups[0].options.map((el, index2) => {
      if (index2 === index) {
        el.selected = true;
        return el;
      } else {
        el.selected = false;
        return el;
      }
    });

    setOptionsGroups(prevOptionsGroups => [
      {
        ...prevOptionsGroups[0],
        options: newOptions,
      },
      // Include the other groups if needed
    ]);

    setSelectedIndex(index);
  };
  useDisableBodyScroll();
  useEffect(() => {
    async function getConfiguration() {
      const configuration = await getOrFetchConfiguration();
      setConfig(configuration);
      setShowBarcode(configuration?.catalog?.product?.details?.showBarcode ?? false);

      return;
    }

    getConfiguration();
  }, []);
  useEffect(() => {
    if (isLoadingProductInfo) return;
    setProductFormatted({
      ...productInfo,
      ...transformProduct(
        productInfo,
        null,
        undefined,
        productTags,
        bundleOffersSummary,
        finalPrice,
        beginPrice,
        flattenIngredientsArray(ingredientDefaultValues),
        personalizedProductsIds,
        a
      ),
    });

    if (productFormatted.optionsGroups?.length > 0) {
      setOptionsGroups(productFormatted?.optionsGroups ?? []);
      setSelectedOptions({});
      setCreatedProducts(initMyoProducts(productFormatted));
    }
    if (
      productInfo.bundleOfferIds &&
      productInfo.bundleOfferIds.length > 0 &&
      productInfo.bundleOfferIds.some(id => bundleOffersSummary.hasOwnProperty(id)) &&
      bundleOffersSummary
    ) {
      setHasBundleOffer(true);
      setBundleDescription(bundleOffersSummary[productInfo.bundleOfferIds[0]]?.description);
    }
    if (process.env.hasItemAdditionMessage) {
      setHasCartAdditionText(removeAddText(productFormatted));
    }
  }, [productInfo, bundleOffersSummary, productsOrder, ingredientDefaultValues, personalizedProductsIds]);

  useEffect(() => {
    if (!isLoadingProductInfo && productFormatted?.optionsGroups?.length > 0) {
      setCreatedProducts(initMyoProducts(productFormatted)); // Ensure the product data is ready
      setOptionsGroups(productFormatted.optionsGroups ?? []);
    }
  }, [productFormatted, isLoadingProductInfo]);

  useEffect(() => {
    if (createdProducts?.length > 0) {
      getOptions(productFormatted?.optionsGroups);
      setComments(createdProducts?.map(el => el?.comments));
    }
  }, [createdProducts]);

  useEffect(() => {
    handleCommentChange(textAreaValue);

    if (createdProducts?.length > 0) {
      setCreatedProducts(prevArray =>
        prevArray.map((item, i) => (i === selectedIndex ? { ...item, comments: textAreaValue } : item))
      );
      setComments(createdProducts?.map(el => el?.comments));
    }
  }, [textAreaValue]);
  useEffect(() => {
    const userListSubscription = UserListsStore.subscribe(setLists);
    const productsForOrder$ = productsForOrder.subscribe(setProductsOrder);

    return () => {
      userListSubscription.unsubscribe();
      productsForOrder$.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (productFormatted)
      setOldPrice(
        productFormatted?.optionsGroups?.length > 0
          ? productFormatted.optionsGroups[0].options[selectedIndex].association?.beginPricePerStepToUom ===
            productFormatted.optionsGroups[0].options[selectedIndex].association?.finalPricePerStepToUom
            ? productFormatted.optionsGroups[0].options[selectedIndex].association?.beginPricePerStepToUom
            : undefined
          : productFormatted.finalPrice !== productFormatted.beginPrice
          ? ((productFormatted.beginPrice * productFormatted.stepToUom) / 100).toFixed(2)
          : undefined
      );
  }, [productFormatted, setOldPrice, selectedIndex]);
  if (!productFormatted) return;

  return (
    <>
      {isLoadingProductDetails || isLoadingProductInfo ? (
        <ProductDetailsAsideSkeleton />
      ) : (
        <div className={cn(productStyles.productsWrapper, styles.productsPadding, styles.maxWidthProducts)}>
          <div className={cn(styles.headerCategories)}>
            <button className={cn(styles.buttonContainerBoth, styles.back)} onClick={onBack}>
              <Icon size={IconSizeEnum.Medium} IconComponent={ArrowBack} iconColor={TextColorEnum.Gray1} />
            </button>

            <Heading color={TextColorEnum.Gray1} type={HeadingEnum.HeadingS} style={{ fontWeight: 700 }}>
              {t('common.asides.side_cart.product_details_header')}
            </Heading>
          </div>
          <div className={cn(productStyles.productColumns, styles.productsOutter)}>
            <div className={cn(productStyles.productColumn, styles.productsInnerPadding)}>
              <ProductDetailsImageSection
                images={images}
                hasOffer={productFormatted.isOnOffer}
                percentage={productFormatted.offerValue}
                shape={process.env.offerShape}
                offerShape={process.env.offerShape}
                size={0}
                hasIcon={process.env.offerHasIcon}
                hasSticker={process.env.offerHasSticker || false}
                classOption={cn(styles.imageSection)}
                productName={productFormatted?.name}
                imageWording={t('product_details.image')}
                thumbnailWording={t('product_details.thumbnail')}
                lightBoxAriaLabels={lightBoxAriaLabels}
              />

              {isMobile ? (
                <div className={productStyles.productDetails}>
                  <ProductCustomization
                    availabilityDays={
                      config?.timeslots?.calculation?.strategy === 'USING_CART_INFO'
                        ? product?.availabilityDays
                        : undefined
                    }
                    availabilityText={
                      config?.timeslots?.calculation?.strategy === 'USING_CART_INFO'
                        ? product?.availability?.type === 'CONSTRAINED'
                          ? describeDeliveryRule(product?.availability?.constrained, undefined, a, true)
                          : product?.availability?.type === 'EXACT'
                          ? createAvailabilityTextDetails(product?.availabilityDays, a, product?.availabilityTimeUnit)
                          : null
                        : null
                    }
                    ingredientDefaultValues={isEmpty(ingredientDefaultValues) ? undefined : ingredientDefaultValues}
                    currencyFormatter={formatter}
                    textAreaDescription={t('product_details.product_customization.comment_placeholder')}
                    qsUnavailableText={t('common.qs.default_unavailable')}
                    qsText={t('common.qs.default')}
                    textAreaTitle={t('product_details.product_customization.comment_label')}
                    product={
                      createdProducts.length > 0 ? createdProducts[selectedIndex] : { ...productFormatted, oldPrice }
                    }
                    codeTitle={t('product_details.product_customization.code_title')}
                    onTextAreaChange={setTextAreaValue}
                    textAreaValue={textAreaValue}
                    onAddToCart={data => {
                      handleProductChange(product, 'add', data);
                      if (data) {
                        handleProductSelect(id, finalPrice, beginPrice, flattenIngredientsArray(data));
                      }
                    }}
                    onRemoveFromCart={data => {
                      handleProductChange(product, 'remove', data);
                    }}
                    onIngredientsAddAsNew={ingredientsAddAsNew}
                    onIngredientUpdateCurrent={ingredientsUpdateCurrent}
                    setMaxSnackBar={setShowSnackBar}
                    offerBannerState="notStarted"
                    offerBannerEmoji="🙌"
                    offerBannerText={bundleDescription}
                    offerBannerInDetails={true}
                    offerBannerButtonText={t('product_details.product_customization.banner_text')}
                    offerBannerOnButtonPress={() => {
                      SelectedBundleOfferStore.update(product);
                      BundleOfferSideSheetStore.update(true);
                    }}
                    hasOfferBanner={hasBundleOffer}
                    tabOptionsList={optionsGroups[0]?.options ?? []}
                    tabOptionsListTitle={
                      optionsGroups[0]?.title ?? t('product_details.product_customization.tab_list_title')
                    }
                    onChipsPress={selectOption}
                    tabOptionsListSubTitle={t('product_details.product_customization.tab_list_subtitle')}
                    selectedIndex={selectedIndex}
                    comments={comments}
                    toastMessage={toastMessage}
                    listClick={item => openListSideSheet(item)}
                    // status={listStatus}
                    listIcon={process.env.listIcon}
                    offerLabelHasRibbon={process.env.offerLabelHasRibbon}
                    showBarcode={showBarcode}
                    backButtonText={t('product_details.product_customization.back_menu')}
                    onBackButtonPress={onBack}
                    cartAddedText={t('product_details.product_customization.in_cart')}
                    hasCartAdditionText={hasCartAdditionText}
                    lowestPriceText={
                      productPriceHistory && shouldDisplayPriceHistory
                        ? t('product_details.product_customization.lowest_price_history', {
                            price: formatter.format(productPriceHistory),
                          })
                        : null
                    }
                    forYouLabel={t('common.user.profile.menu.for_you')}
                    isPackagingFeeEnabled={configuration?.catalog?.product?.packagingFee?.enabled}
                    packagedProduct={
                      configuration?.catalog?.product?.packagingFee?.enabled
                        ? { name: packagedProduct?.name, finalPrice: packagedProduct?.finalPrice }
                        : null
                    }
                    packagingFeeIconType={process.env.packagingFeeIconType}
                  />
                </div>
              ) : null}
              <div className={cn(productStyles.productDetails, productStyles.productDescription)}>
                <ProductInfo
                  descriptionTitle={
                    productDetails?.description
                      ? t('product_details.product_info.default_description_title')
                      : undefined
                  }
                  description={productDetails?.description}
                  isCollapsible
                />
                {productDetails?.videos &&
                  productDetails?.videos.length > 0 &&
                  productDetails?.videos.map(video => (
                    <ProductVideo
                      key={video.index}
                      // TODO: Add proper video description
                      // description={productDetails?.description}
                      videoUrl={video.url}
                      descriptionTitle={t('product_details.product_video.default_description_title')}
                      isCollapsible
                    />
                  ))}

                {shouldDisplayNutritionFacts ? (
                  <div className={productStyles.productDetailsNoMargin}>
                    <NutritionFacts
                      isCollapsible
                      nutritionItems={productDetails?.nutritionItems}
                      moreText={a('common.nutrition_facts.see_more')}
                      lessText={a('common.nutrition_facts.see_less')}
                    />
                  </div>
                ) : null}
                {/* {shouldDisplayAdditionalInfo ? (
                <AdditionalInfo
                  instructions={productDetails?.instructions}
                  instructionsTitle={productDetails?.instructions ? 'Οδηγίες Χρήσης / Αποθήκευσης' : undefined}
                  madeIn={productDetails?.madeIn}
                  madeInTitle={productDetails?.madeIn ? 'Χώρα Παραγωγής' : undefined}
                  supplierInfo={productDetails?.supplierInfo}
                  supplierInfoTitle={productDetails?.supplierInfo ? 'Πληροφορίες προμηθευτή' : undefined}
                  smallDescription={productDetails?.smallDescription}
                  smallDescriptionTitle={productDetails?.smallDescription ? 'Μικρή περιγραφή' : undefined}
                />
              ) : null}*/}

                {shouldDisplayProductContentGroups
                  ? productDetails?.contentGroups
                      ?.sort?.((a, b) => a.index - b.index)
                      ?.map?.((cg, index) => (
                        <ProductContentGroup key={index} index={cg.index} items={cg.items} title={cg.title} />
                      ))
                  : null}
              </div>
            </div>

            {isMobile ? null : (
              <div>
                <ProductCustomization
                  availabilityDays={
                    config?.timeslots?.calculation?.strategy === 'USING_CART_INFO'
                      ? product?.availabilityDays
                      : undefined
                  }
                  availabilityText={
                    config?.timeslots?.calculation?.strategy === 'USING_CART_INFO'
                      ? product?.availability?.type === 'CONSTRAINED'
                        ? describeDeliveryRule(product?.availability?.constrained, undefined, a, true)
                        : product?.availability?.type === 'EXACT'
                        ? createAvailabilityTextDetails(product?.availabilityDays, a, product?.availabilityTimeUnit)
                        : null
                      : null
                  }
                  ingredientDefaultValues={isEmpty(ingredientDefaultValues) ? undefined : ingredientDefaultValues}
                  currencyFormatter={formatter}
                  textAreaDescription={t('product_details.product_customization.comment_placeholder')}
                  qsUnavailableText={t('common.qs.default_unavailable')}
                  qsText={t('common.qs.default')}
                  textAreaTitle={t('product_details.product_customization.comment_label')}
                  product={
                    createdProducts.length > 0 ? createdProducts[selectedIndex] : { ...productFormatted, oldPrice }
                  }
                  codeTitle={t('product_details.product_customization.code_title')}
                  onTextAreaChange={setTextAreaValue}
                  textAreaValue={textAreaValue}
                  onAddToCart={data => {
                    handleProductChange(product, 'add', data);
                    if (data) {
                      handleProductSelect(id, finalPrice, beginPrice, flattenIngredientsArray(data));
                    }
                  }}
                  onRemoveFromCart={data => {
                    handleProductChange(product, 'remove', data);
                  }}
                  onIngredientsAddAsNew={ingredientsAddAsNew}
                  onIngredientUpdateCurrent={ingredientsUpdateCurrent}
                  offerBannerState="notStarted"
                  offerBannerEmoji="🙌"
                  offerBannerText={bundleDescription}
                  offerBannerInDetails={true}
                  setMaxSnackBar={setShowSnackBar}
                  offerBannerButtonText={t('product_details.product_customization.banner_text')}
                  offerBannerOnButtonPress={() => {
                    SelectedBundleOfferStore.update(productFormatted);
                    BundleOfferSideSheetStore.update(true);
                  }}
                  hasOfferBanner={hasBundleOffer}
                  tabOptionsList={optionsGroups[0]?.options ?? []}
                  tabOptionsListTitle={
                    optionsGroups[0]?.title ?? t('product_details.product_customization.tab_list_title')
                  }
                  onChipsPress={selectOption}
                  tabOptionsListSubTitle={t('product_details.product_customization.tab_list_subtitle')}
                  selectedIndex={selectedIndex}
                  comments={comments}
                  toastMessage={toastMessage}
                  listClick={item => {
                    openListSideSheet(item);
                  }}
                  // status={listStatus}
                  listIcon={process.env.listIcon}
                  offerLabelHasRibbon={process.env.offerLabelHasRibbon}
                  showBarcode={showBarcode}
                  backButtonText={t('product_details.product_customization.back_menu')}
                  onBackButtonPress={onBack}
                  cartAddedText={t('product_details.product_customization.in_cart')}
                  hasCartAdditionText={hasCartAdditionText}
                  lowestPriceText={
                    productPriceHistory && shouldDisplayPriceHistory
                      ? t('product_details.product_customization.lowest_price_history', {
                          price: formatter.format(productPriceHistory),
                        })
                      : null
                  }
                  forYouLabel={t('common.user.profile.menu.for_you')}
                  isPackagingFeeEnabled={configuration?.catalog?.product?.packagingFee?.enabled}
                  packagedProduct={
                    configuration?.catalog?.product?.packagingFee?.enabled
                      ? { name: packagedProduct?.name, finalPrice: packagedProduct?.finalPrice }
                      : null
                  }
                  packagingFeeIconType={process.env.packagingFeeIconType}
                />
              </div>
            )}
          </div>

          {/* {frequent && (
            <section className={productStyles.bottomAisleContainer}>
              <ProductAisleV2
                title={t('product_details.frequently_bought_together.title')}
                classOption={productStyles.bottomAisle}
                products={frequent}
                hasPadding={true}
                isMobile={isMobile}
                showMore={false}
                onListClick={openListSideSheet}
                setMaxSnackBar={setShowSnackBar}
                addProduct={product =>
                  addProduct(product, true, {
                    item_list_id: `bought_together`,
                    item_list_name: `Bought Together`,
                  })
                }
                removeProduct={product =>
                  removeProduct(product, {
                    item_list_id: `bought_together`,
                    item_list_name: `Bought Together`,
                  })
                }
                tilesSize="default"
                horizontal
                LinkComponent={props => {
                  const propsWithCleanUrl = getProductPropsWithCleanUrl(props);

                  return (
                    <Link
                      prefetch={false}
                      {...propsWithCleanUrl}
                      onClick={() => {
                        const product = getProductBasedOnSlug(frequent, propsWithCleanUrl.href);

                        if (product) {
                          selectItem(getProductInfo(product), {
                            item_list_id: `bought_together`,
                            item_list_name: `Bought Together`,
                            index: getProductIndexBasedOnSlug(propsWithCleanUrl.href) + 1,
                          });
                        }
                      }}
                    />
                  );
                }}
                defaultQsText={t('common.qs.default')}
                defaultQsUnavailableText={t('common.qs.default_unavailable')}
                ImageComponent={CustomImage}
                slideToMoveTo={slideIndex}
                offerLabelHasRibbon={process.env.offerLabelHasRibbon}
                isTileDetailsAnimated={process.env.isTileDetailsAnimated}
                ctaIcon={process.env.ctaIcon}
                listIcon={process.env.listIcon}
              />
            </section>
          )}

          {similar && (
            <section className={productStyles.bottomAisleContainer}>
              <ProductAisleV2
                title={t('product_details.similar_products.title')}
                classOption={productStyles.bottomAisle}
                products={similar}
                hasPadding={true}
                isMobile={isMobile}
                showMore={false}
                onListClick={openListSideSheet}
                setMaxSnackBar={setShowSnackBar}
                addProduct={product =>
                  addProduct(product, true, {
                    item_list_id: `similar`,
                    item_list_name: `Similar`,
                  })
                }
                removeProduct={product =>
                  removeProduct(product, {
                    item_list_id: `similar`,
                    item_list_name: `Similar`,
                  })
                }
                tilesSize="default"
                horizontal
                LinkComponent={props => {
                  const propsWithCleanUrl = getProductPropsWithCleanUrl(props);

                  return (
                    <Link
                      prefetch={false}
                      {...propsWithCleanUrl}
                      onClick={() => {
                        const product = getProductBasedOnSlug(similar, propsWithCleanUrl.href);

                        if (product) {
                          selectItem(getProductInfo(product), {
                            item_list_id: `similar`,
                            item_list_name: `Similar`,
                            index: getProductIndexBasedOnSlug(propsWithCleanUrl.href) + 1,
                          });
                        }
                      }}
                    />
                  );
                }}
                defaultQsText={t('common.qs.default')}
                defaultQsUnavailableText={t('common.qs.default_unavailable')}
                ImageComponent={CustomImage}
                slideToMoveTo={slideIndex}
                offerLabelHasRibbon={process.env.offerLabelHasRibbon}
                isTileDetailsAnimated={process.env.isTileDetailsAnimated}
                ctaIcon={process.env.ctaIcon}
                listIcon={process.env.listIcon}
              />
            </section>
          )} */}
          <SnackBar open={showSnackBar} setOpen={setShowSnackBar} text={t('common.snackbar')} position={'center'} />
        </div>
      )}
    </>
  );
}
